<template>
    <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400" title="Needles"
        :is_open="true" :modified="multipleMode || datasetMode">
        <template v-if="datasetMode">
            <p class="text-xs text-slate-300">
                Managed by dataset
                <span class="font-semibold text-white">{{ dataset?.name || node.dataset }}</span>.
            </p>
            <p v-if="!dataset" class="text-xs text-amber-400">
                Dataset not found. Select another Dataset above.
            </p>
            <div v-for="needle in datasetNeedles" :key="needle.index"
                class="flex items-center gap-2 px-2 py-1 text-xs rounded bg-slate-700/60">
                <span class="w-4 text-right text-slate-400">{{ needle.index + 1 }}</span>
                <span class="inline-block w-3 h-3 rounded-sm border border-slate-600 flex-shrink-0"
                    :style="{ background: needle.color || '#4da6ff' }"></span>
                <span class="flex-1 truncate text-slate-200">{{ needle.label }}</span>
                <span class="font-mono text-slate-400">{{ needle.val }}</span>
            </div>
            <p v-if="dataset && datasetNeedles.length === 0" class="text-xs text-slate-500">
                No series defined in this dataset.
            </p>
        </template>

        <template v-else>
            <div class="flex items-center justify-between">
                <span class="text-sm text-white">Manual needles</span>
                <button type="button" class="text-white" title="Add needle" @click="addNeedle">
                    <font-awesome-icon icon="fa-solid fa-plus-circle" />
                </button>
            </div>
            <p v-if="!multipleMode" class="text-xs text-slate-300">
                Needle 0 uses the legacy <code>val</code> and Indicator color.
            </p>

            <div v-for="(needle, index) in editorNeedles" :key="editorKey(needle, index)"
                class="p-2 space-y-1 rounded bg-slate-700/60">
                <div class="flex items-center justify-between">
                    <span class="text-xs font-semibold text-white">Needle {{ index }}</span>
                    <div v-if="multipleMode" class="flex items-center gap-1">
                        <button type="button" class="px-1 text-xs text-slate-300 disabled:opacity-30"
                            :disabled="index === 0" title="Move needle up" @click="moveNeedle(index, -1)">↑</button>
                        <button type="button" class="px-1 text-xs text-slate-300 disabled:opacity-30"
                            :disabled="index === needles.length - 1" title="Move needle down" @click="moveNeedle(index, 1)">↓</button>
                        <button type="button" class="px-1 text-red-400 disabled:opacity-30"
                            :disabled="needles.length <= 1" title="Remove needle" @click="removeNeedle(index)">✕</button>
                    </div>
                </div>
                <property-row label="Value">
                    <form-input type="number" :no_scroll="true" :min="node.min" :max="node.max"
                        :model-value="needle.val" @update:model-value="updateValue(needle, $event, index)" />
                </property-row>
                <property-row label="color">
                    <ColorPicker :pureColor="needle.color || fallbackColor"
                        useType="pure" format="hex" lang="En" shape="circle"
                        @pureColorChange="updateColor(needle, $event, index)" />
                </property-row>
            </div>
        </template>
    </Accordion>
</template>

<script>
import { ColorPicker } from 'vue3-colorpicker'
import 'vue3-colorpicker/style.css'
import Accordion from '../Accordion.vue'
import PropertyRow from './PropertyRow.vue'
import { useDatasetStore } from '../../stores/datasetStore.js'
import { propertyEditorKey } from './propertyEditorKey.js'
import {
    ensureGaugeNeedleIds,
    getGaugeNeedleId,
    getGaugeDatasetNeedles,
    moveGaugeNeedle,
    removeGaugeNeedle,
} from '../../Hasp/gaugeNeedles.js'
import {
    addGaugeNeedle,
    getGaugeNeedleEditorRows,
} from './gaugeNeedleEditor.js'

export default {
    name: 'GaugeNeedlesEditor',
    components: { Accordion, ColorPicker, PropertyRow },
    props: { node: { type: Object, required: true } },
    data() {
        return { revision: 0, datasetStore: useDatasetStore() }
    },
    computed: {
        multipleMode() {
            void this.revision
            return Array.isArray(this.node.needles)
        },
        datasetMode() {
            void this.revision
            return !!this.node.dataset
        },
        dataset() {
            void this.revision
            return this.datasetMode ? this.datasetStore.getDataset(this.node.dataset) : null
        },
        needles() {
            void this.revision
            const needles = Array.isArray(this.node.needles) ? this.node.needles : []
            ensureGaugeNeedleIds(needles)
            return needles
        },
        fallbackColor() {
            void this.revision
            return this.node.style('indicator').line_color
        },
        editorNeedles() {
            void this.revision
            return getGaugeNeedleEditorRows(this.node, this.fallbackColor)
        },
        datasetNeedles() {
            void this.revision
            return this.dataset
                ? getGaugeDatasetNeedles(this.dataset, this.node.min, this.node.max, this.fallbackColor)
                : []
        },
    },
    mounted() {
        this.subscribeNode(this.node)
    },
    beforeUnmount() {
        this.unsubscribeNode(this.node)
    },
    watch: {
        node(nextNode, previousNode) {
            this.unsubscribeNode(previousNode)
            this.subscribeNode(nextNode)
            this.revision++
        },
    },
    methods: {
        editorKey(needle, index) {
            const identity = this.multipleMode ? getGaugeNeedleId(needle) : 'legacy-0'
            return propertyEditorKey(this.node, `gauge-needle:${identity ?? index}`)
        },
        subscribeNode(node) {
            node?.on?.('needles.gauge-needles-editor', this.refresh)
            node?.on?.('dataset.gauge-needles-editor', this.refresh)
            node?.on?.('data_val.gauge-needles-editor', this.refresh)
            node?.onStyle?.('indicator', 'line_color', this.refresh)
        },
        unsubscribeNode(node) {
            node?.off?.('needles.gauge-needles-editor', this.refresh)
            node?.off?.('dataset.gauge-needles-editor', this.refresh)
            node?.off?.('data_val.gauge-needles-editor', this.refresh)
            node?.offStyle?.('indicator', 'line_color', this.refresh)
        },
        refresh() {
            this.revision++
        },
        notifyNeedles() {
            this.node.adjust('needles')
            this.revision++
        },
        addNeedle() {
            addGaugeNeedle(this.node, this.fallbackColor)
            this.notifyNeedles()
        },
        updateValue(needle, value, index) {
            if (!this.multipleMode && index === 0) {
                this.node.val = Number(value)
                this.node.adjust('data_val')
            } else {
                needle.val = Number(value)
                this.notifyNeedles()
            }
        },
        updateColor(needle, color) {
            if (!this.multipleMode) {
                this.node.setStyle('indicator', 'line_color', color)
                this.refresh()
            } else {
                needle.color = color
                this.notifyNeedles()
            }
        },
        removeNeedle(index) {
            this.node.needles = removeGaugeNeedle(this.needles, index)
            this.notifyNeedles()
        },
        moveNeedle(index, delta) {
            this.node.needles = moveGaugeNeedle(this.needles, index, delta)
            this.notifyNeedles()
        },
    },
}
</script>
